import React, { useRef, useState } from "react";
import { Field, ErrorMessage } from "formik";
import { get, set } from "lodash";
import { IMAGE_FETCH_URL, IMAGE_URL } from "../../config";
import { formatUnderscoreToSpace } from "../../utils/text-format";
import {
  UploadCloud,
  X,
  Eye,
  FileText,
  Film,
  Image as ImageIcon,
  ExternalLink,
  Trash2,
  Loader2,
  Plus,
} from "lucide-react";

export const FormMultiFileUploadField = ({
  label = "Upload Files",
  name = "files",
  labelClassName = "",
  className = "",
  previewClassName = "",
  accept = "*",
  maxSizeMB = 10,
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [isDragActive, setIsDragActive] = useState(false);
  const [previewMedia, setPreviewMedia] = useState(null);

  const inputRef = useRef(null);

  // Helper to determine file category
  const getFileType = (fileName = "", url = "") => {
    const ext = (fileName || url).split(".").pop().toLowerCase();
    if (["jpg", "jpeg", "png", "gif", "webp", "svg"].includes(ext))
      return "image";
    if (["mp4", "webm", "ogg", "mov"].includes(ext)) return "video";
    return ext || "file";
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return "";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + " " + sizes[i];
  };

  const uploadFiles = async (filesArray, form, name) => {
    const files = Array.from(filesArray || []);
    if (!files.length) return;

    const MAX_SIZE = maxSizeMB * 1024 * 1024;
    const validFiles = files.filter((file) => file.size <= MAX_SIZE);

    if (validFiles.length < files.length) {
      alert(`Some files exceed the ${maxSizeMB}MB size limit.`);
    }

    if (!validFiles.length) return;

    setIsUploading(true);
    const uploaded = [];

    for (const file of validFiles) {
      const formData = new FormData();
      formData.append("file", file);

      try {
        const response = await fetch(IMAGE_URL, {
          method: "POST",
          body: formData,
        });

        if (response.ok) {
          const data = await response.json();
          const filePath = data?.data?.filePath || data?.filePath;
          const fullUrl = filePath.startsWith("http")
            ? filePath
            : `${IMAGE_FETCH_URL}${filePath}`;

          uploaded.push({
            url: fullUrl,
            name: file.name,
            size: file.size,
            type: file.type,
          });
        } else {
          alert(`Failed to upload ${file.name}`);
        }
      } catch (err) {
        console.error(`Error uploading ${file.name}:`, err);
        alert(`An error occurred while uploading ${file.name}`);
      }
    }

    setIsUploading(false);

    const currentFiles = get(form.values, name) || [];
    const updatedValues = { ...form.values };
    set(updatedValues, name, [...currentFiles, ...uploaded]);
    form.setValues(updatedValues);
    form.setFieldTouched(name, true, false);

    if (inputRef.current) inputRef.current.value = "";
  };

  const handleFileChange = (event, form, name) => {
    uploadFiles(event.target.files, form, name);
  };

  const handleDrop = (event, form, name) => {
    event.preventDefault();
    setIsDragActive(false);
    uploadFiles(event.dataTransfer?.files, form, name);
  };

  const handleRemoveFile = (form, name, index) => {
    const files = get(form.values, name) || [];
    const updated = [...files.slice(0, index), ...files.slice(index + 1)];
    const updatedValues = { ...form.values };
    set(updatedValues, name, updated);
    form.setValues(updatedValues);
    form.setFieldTouched(name, true, false);
  };

  return (
    <Field name={name}>
      {({ form, meta }) => {
        const files = get(form.values, name) || [];
        const error = meta.touched && meta.error;

        return (
          <div className={`flex flex-col gap-1.5 w-full ${className}`}>
            {/* Header & Add Button */}
            <div className="flex items-center justify-between mb-1">
              {label && (
                <div>
                  <label
                    htmlFor={name}
                    className={`text-sm font-semibold text-gray-800 capitalize ${
                      error ? "text-red-500" : labelClassName
                    }`}
                  >
                    {formatUnderscoreToSpace(label)}
                  </label>
                  {files.length > 0 && (
                    <p className="text-xs text-gray-500">
                      {files.length} {files.length === 1 ? "file" : "files"}{" "}
                      uploaded
                    </p>
                  )}
                </div>
              )}

              {files.length > 0 && (
                <label className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-primary hover:bg-indigo-700 active:bg-indigo-800 rounded-lg cursor-pointer transition shadow-xs">
                  <Plus className="w-4 h-4" />
                  Add <span className="hidden md:block">Files</span>
                  <input
                    ref={inputRef}
                    type="file"
                    accept={accept}
                    multiple
                    className="hidden"
                    onChange={(event) => handleFileChange(event, form, name)}
                  />
                </label>
              )}
            </div>

            {/* Dropzone Container */}
            <div
              className={`relative rounded-xl border-2 border-dashed p-6 transition-all cursor-pointer min-h-[140px] flex flex-col justify-center items-center ${
                isDragActive
                  ? "border-indigo-500 bg-indigo-50/50"
                  : "border-gray-200 bg-gray-50/50 hover:border-gray-300 hover:bg-gray-100/50"
              } ${error ? "border-red-300 bg-red-50/20" : ""}`}
              onDragOver={(e) => {
                e.preventDefault();
                setIsDragActive(true);
              }}
              onDragLeave={() => setIsDragActive(false)}
              onDrop={(e) => handleDrop(e, form, name)}
              onClick={() => inputRef.current?.click()}
            >
              {isUploading ? (
                <div className="flex flex-col items-center justify-center gap-2">
                  <Loader2 className="w-7 h-7 animate-spin text-primary" />
                  <p className="text-xs font-semibold text-gray-700">
                    Uploading files...
                  </p>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center text-center">
                  <div className="w-10 h-10 rounded-full bg-indigo-50 flex items-center justify-center text-primary mb-2">
                    <UploadCloud className="w-5 h-5" />
                  </div>
                  <p className="text-xs font-semibold text-gray-700">
                    Click to upload or drag & drop files
                  </p>
                  <p className="text-[11px] text-gray-400 mt-1">
                    Up to {maxSizeMB}MB per file
                  </p>
                </div>
              )}

              <input
                ref={inputRef}
                type="file"
                accept={accept}
                multiple
                className="hidden"
                onChange={(event) => handleFileChange(event, form, name)}
              />
            </div>

            {/* File Previews List */}
            {files.length > 0 && (
              <div
                className={`grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2 ${previewClassName}`}
              >
                {files.map((file, index) => {
                  const fileCategory = getFileType(file.name, file.url);

                  return (
                    <div
                      key={index}
                      className="group relative rounded-xl border border-gray-200 bg-white p-3 shadow-xs transition hover:shadow-md flex items-center justify-between gap-3"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        {/* Preview Thumbnail / Extension Badge */}
                        <div className="relative flex-shrink-0 w-11 h-11 rounded-lg overflow-hidden border border-gray-100 bg-gray-50 flex items-center justify-center">
                          {fileCategory === "image" ? (
                            <img
                              src={file.url}
                              alt={file.name}
                              className="w-full h-full object-cover"
                            />
                          ) : fileCategory === "video" ? (
                            <div className="relative w-full h-full bg-gray-900 flex items-center justify-center">
                              <video
                                src={file.url}
                                className="w-full h-full object-cover opacity-60"
                              />
                              <Film className="absolute w-4 h-4 text-white" />
                            </div>
                          ) : (
                            <div className="flex flex-col items-center justify-center">
                              <FileText className="w-4 h-4 text-primary mb-0.5" />
                              <span className="text-[8px] font-bold uppercase text-gray-500 max-w-[32px] truncate">
                                {fileCategory}
                              </span>
                            </div>
                          )}
                        </div>

                        {/* File Name & Details */}
                        <div className="min-w-0 flex-1">
                          <p
                            className="text-xs font-semibold text-gray-800 truncate"
                            title={file.name}
                          >
                            {file.name}
                          </p>
                          <p className="text-[11px] text-gray-400 mt-0.5">
                            {file.size
                              ? formatFileSize(file.size)
                              : "Uploaded file"}
                          </p>
                        </div>
                      </div>

                      {/* Item Action Buttons */}
                      <div className="flex items-center gap-1">
                        {(fileCategory === "image" ||
                          fileCategory === "video") && (
                          <button
                            type="button"
                            onClick={() =>
                              setPreviewMedia({
                                url: file.url,
                                type: fileCategory,
                                name: file.name,
                              })
                            }
                            className="p-1.5 rounded-lg text-gray-500 hover:text-primary hover:bg-indigo-50 transition"
                            title="Preview"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                        )}

                        <a
                          href={file.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-1.5 rounded-lg text-gray-500 hover:text-primary hover:bg-indigo-50 transition"
                          title="Open in new tab"
                        >
                          <ExternalLink className="w-4 h-4" />
                        </a>

                        <button
                          type="button"
                          onClick={() => handleRemoveFile(form, name, index)}
                          className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 transition"
                          title="Remove file"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Lightbox Modal */}
            {previewMedia && (
              <div
                className="fixed inset-0 z-[5001] flex items-center justify-center bg-black/80 p-4 backdrop-blur-xs"
                onClick={() => setPreviewMedia(null)}
              >
                <div
                  className="relative max-h-[90vh] max-w-[90vw] overflow-hidden rounded-xl bg-gray-900 shadow-2xl flex items-center justify-center"
                  onClick={(e) => e.stopPropagation()}
                >
                  <button
                    type="button"
                    onClick={() => setPreviewMedia(null)}
                    className="absolute right-3 top-3 z-10 rounded-full bg-black/50 p-2 text-white hover:bg-black/70 transition"
                  >
                    <X className="w-5 h-5" />
                  </button>
                  {previewMedia.type === "video" ? (
                    <video
                      src={previewMedia.url}
                      controls
                      autoPlay
                      className="max-h-[85vh] max-w-full rounded-lg"
                    />
                  ) : (
                    <img
                      src={previewMedia.url}
                      alt={previewMedia.name || "Preview"}
                      className="max-h-[85vh] max-w-full object-contain rounded-lg p-2"
                    />
                  )}
                </div>
              </div>
            )}

            {/* Formik Validation Error */}
            <div className="min-h-[1.25rem] px-1">
              <ErrorMessage name={name}>
                {(msg) => (
                  <span className="block text-xs font-medium text-red-500">
                    {msg}
                  </span>
                )}
              </ErrorMessage>
            </div>
          </div>
        );
      }}
    </Field>
  );
};
